import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { wikiLogout, getWikiNickname, isWikiAdmin, hasWikiPermission } from '../../utils/wikiAuth';
import { createUser } from '../../api/wikiApi';
import SimpleModal from './SimpleModal';
import { WIKI_PERMISSIONS } from './wikiPermissions';
import { LayoutDashboard, FileText, History, LogOut, Menu, X, BookOpen, FolderTree, UserPlus, BarChart2, HelpCircle, Map, Trophy, Users, ArrowUpRight } from 'lucide-react';
import styles from './WikiAdminPanel.module.css';

export const WikiAdminAuthContext = React.createContext(null);
export const useWikiAdminAuth = () => React.useContext(WikiAdminAuthContext) || {};
const initialForm = () => ({ login: '', password: '', displayName: '', role: 'ROLE_EDITOR', permissions: ['MANAGE_PAGES', 'MANAGE_CATEGORIES', 'MANAGE_IMAGES'] });
const menu = [
  ['/wiki/admin', LayoutDashboard, 'Обзор', null, true],
  ['/wiki/admin/pages', FileText, 'Статьи', 'MANAGE_PAGES'],
  ['/wiki/admin/categories', FolderTree, 'Разделы', 'MANAGE_CATEGORIES'],
  ['/wiki/admin/history', History, 'История правок', 'ROLE_ADMIN'],
  ['/wiki/admin/analytics', BarChart2, 'Аналитика', 'VIEW_ANALYTICS'],
  ['/wiki/admin/tournaments', Trophy, 'Турниры', 'MANAGE_TOURNAMENTS'],
  ['/wiki/admin/map', Map, 'Карта мира', 'MANAGE_MAP'],
  ['/wiki/admin/users', Users, 'Пользователи', 'MANAGE_USERS'],
  ['/wiki/admin/gallery', FileText, 'Галерея игроков', 'ROLE_ADMIN'],
  ['/wiki/admin/guide', HelpCircle, 'Помощь редактору', null],
];

export default function WikiAdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [authError, setAuthError] = useState(null);
  const nickname = getWikiNickname();
  const isAdmin = isWikiAdmin();
  const items = menu.filter(([, , , permission]) => !permission || (permission === 'ROLE_ADMIN' ? isAdmin : hasWikiPermission(permission)));
  const title = menu.find(([path, , , , exact]) => exact ? location.pathname === path : location.pathname.startsWith(path))?.[2] || 'Вики';
  const logout = () => { wikiLogout(); navigate('/wiki/admin/login'); };
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    const close = () => setSidebarOpen(false);
    window.addEventListener('wikiAdminEditorOpen', close);
    return () => window.removeEventListener('wikiAdminEditorOpen', close);
  }, []);
  useEffect(() => {
    if (!sidebarOpen) return;
    const close = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [sidebarOpen]);
  async function submit(e) {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await createUser({ ...form, login: form.login.trim(), displayName: form.displayName.trim() || form.login.trim() });
      setCreating(false); setForm(initialForm());
      window.dispatchEvent(new Event('wikiUsersChanged'));
      navigate('/wiki/admin/users');
    } catch (err) { setError(err.message || 'Не удалось создать пользователя'); }
    finally { setSaving(false); }
  }
  const nav = (mobile = false) => items.map(([path, Icon, label, , exact]) => (
    <NavLink key={path} to={path} end={exact} className={({ isActive }) => `${mobile ? styles.mobileTab : styles.navLink} ${isActive ? styles.navLinkActive : ''}`} onClick={() => setSidebarOpen(false)}>
      <Icon size={18} /><span>{label}</span>
    </NavLink>
  ));
  return <div className={styles.adminContainer}>
    <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
      <div className={styles.sidebarHeader}>
        <NavLink to="/wiki/admin" className={styles.logo}><BookOpen size={28} /><span>InterWorld<small>ВИКИ · РЕДАКЦИЯ</small></span></NavLink>
        <button className={styles.closeButton} onClick={() => setSidebarOpen(false)} aria-label="Закрыть меню"><X /></button>
      </div>
      <div className={styles.navCaption}>РАБОЧЕЕ ПРОСТРАНСТВО</div>
      <nav className={styles.nav} aria-label="Управление Вики">{nav()}</nav>
      <div className={styles.sidebarFooter}>
        <a href="/wiki" className={styles.siteLink}>Открыть Вики <ArrowUpRight size={16} /></a>
        <div className={styles.userInfo}><span className={styles.userAvatar}>{nickname?.slice(0, 1).toUpperCase() || 'Р'}</span><div className={styles.userName}>{nickname}<small>{isAdmin ? 'Администратор' : 'Редактор'}</small></div><button className={styles.logoutButton} onClick={logout} aria-label="Выйти"><LogOut size={18} /></button></div>
      </div>
    </aside>
    {sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)} aria-hidden="true" />}
    <div className={styles.mainContent}>
      <header className={styles.header}>
        <button className={styles.menuButton} onClick={() => setSidebarOpen(true)} aria-label="Открыть меню" aria-expanded={sidebarOpen}><Menu /></button>
        <div className={styles.headerTitle}><span>Вики</span><span>/</span>{title}</div>
        {hasWikiPermission('MANAGE_USERS') && <button className={styles.createUserButton} aria-label="Добавить редактора" onClick={() => { setError(''); setForm(initialForm()); setCreating(true); }}><UserPlus size={17} /><span>Добавить редактора</span></button>}
      </header>
      <nav className={styles.mobileTabs} aria-label="Разделы админки">{nav(true)}</nav>
      <WikiAdminAuthContext.Provider value={{ onAuthError: setAuthError }}><main className={styles.content}><Outlet /></main></WikiAdminAuthContext.Provider>
    </div>
    {creating && <SimpleModal title="Новый редактор" onClose={() => !saving && setCreating(false)}>
      <form className={styles.createUserForm} onSubmit={submit}>
        <p className={styles.formHint}>Создайте учётную запись и выберите, с чем сможет работать редактор.</p>
        {error && <div className={styles.createUserError} role="alert">{error}</div>}
        <label>Логин<input required autoComplete="off" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} /></label>
        <label>Имя в истории правок<input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></label>
        <label>Пароль<input required type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
        <fieldset className={styles.permissions}><legend>Права доступа</legend>{WIKI_PERMISSIONS.map(({ key, label }) => <label key={key}><input type="checkbox" checked={form.permissions.includes(key)} onChange={(e) => setForm({ ...form, permissions: e.target.checked ? [...form.permissions, key] : form.permissions.filter((p) => p !== key) })} />{label}</label>)}</fieldset>
        <div className={styles.createUserActions}><button type="button" className={styles.createUserCancel} disabled={saving} onClick={() => setCreating(false)}>Отмена</button><button className={styles.createUserSubmit} disabled={saving}>{saving ? 'Создание…' : 'Создать редактора'}</button></div>
      </form>
    </SimpleModal>}
    {authError && <SimpleModal title="Доступ к Вики" onClose={() => setAuthError(null)}><p>{authError.message || 'Сессия истекла или у вас недостаточно прав.'}</p><button className={styles.createUserSubmit} onClick={logout}>Войти заново</button></SimpleModal>}
  </div>;
}
